import * as XLSX from "xlsx";
import { autoDetectMapping, detectByContent } from "@/lib/contact-import-parse";
import { useState, useRef, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import {
  Upload, Download, ArrowRight, ArrowLeft, Check, X, AlertTriangle,
  FileSpreadsheet, Rocket, Plus, Tags as TagsIcon, ChevronDown, ChevronRight as ChevronRightIcon,
  Pencil, CalendarDays,
} from "lucide-react";
import ImportHistoryTab from "./ImportHistoryTab";
import ExportContactsTab from "./ExportContactsTab";

// ─── Types ───────────────────────────────────────────────────────────────────

type ImportStep = "upload" | "mapping" | "preview" | "importing" | "report";

type DuplicateStrategy = "skip" | "update" | "allow";

type FieldMapping = {
  sourceColumn: string;
  targetField: string;
};

type RowValidation = {
  originalIndex: number;
  row: Record<string, string>;
  mapped: Record<string, string>;
  status: "valid" | "warning" | "error";
  errors: string[];
  warnings: string[];
};

type ImportReport = {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  duplicates: number;
  errorDetails: string[];
};

// ─── System fields ───────────────────────────────────────────────────────────

// `required` marca o campo com asterisco na tela. Telefone saiu da lista: a
// regra agora é Nome + (Telefone OU E-mail), e nenhum dos dois canais é
// obrigatório sozinho — ver `canProceed`.
const SYSTEM_FIELDS = [
  { key: "phoneNumber", label: "Telefone", required: false },
  { key: "name", label: "Nome", required: true },
  { key: "lastName", label: "Sobrenome", required: false },
  { key: "email", label: "E-mail", required: false },
  { key: "contactType", label: "Tipo de Contato", required: false },
  { key: "companyName", label: "Orgao / Empresa", required: false },
  { key: "companyType", label: "Tipo de Orgao", required: false },
  { key: "sector", label: "Setor / Secretaria", required: false },
  { key: "jobTitle", label: "Cargo", required: false },
  { key: "city", label: "Cidade", required: false },
  { key: "state", label: "Estado (UF)", required: false },
  { key: "notes", label: "Observacoes", required: false },
] as const;

// ─── Title Case PT-BR (espelha titleCaseName do backend) ─────────────────────
const NAME_LOWERCASE_PARTS = new Set(["da", "de", "di", "do", "du", "das", "dos", "e", "y"]);

function titleCaseName(input: string): string {
  if (!input) return input;
  return input
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((word, idx) => {
      if (word.length === 0) return word;
      if (idx > 0 && NAME_LOWERCASE_PARTS.has(word)) return word;
      return word
        .split(/([-'])/)
        .map(part => part.length > 0 && /^[a-zà-ÿ]/i.test(part)
          ? part[0].toUpperCase() + part.slice(1)
          : part)
        .join("");
    })
    .join(" ");
}

// ─── Phone normalization (client-side) ───────────────────────────────────────

function normalizePhone(raw: string): { normalized: string; valid: boolean; warning?: string } {
  let cleaned = raw.replace(/[\s\-\(\)\+\.]/g, "");
  if (!/^\d+$/.test(cleaned)) return { normalized: raw, valid: false, warning: "Caracteres invalidos" };
  if (cleaned.length < 10) return { normalized: raw, valid: false, warning: "Muito curto" };
  if (cleaned.length > 15) return { normalized: raw, valid: false, warning: "Muito longo" };

  if (!cleaned.startsWith("55") && cleaned.length === 11) {
    cleaned = "55" + cleaned;
  }

  if (cleaned.length === 13 && cleaned.startsWith("55")) {
    return { normalized: cleaned, valid: true };
  }

  if (cleaned.length >= 10 && cleaned.length <= 15) {
    return { normalized: cleaned, valid: true, warning: "Formato pode nao ser brasileiro" };
  }

  return { normalized: cleaned, valid: false, warning: "Formato invalido" };
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// ─── Component (full-page, no dialog) ────────────────────────────────────────

export default function ContactImportWizard({
  onClose,
  onImportDone,
}: {
  onClose: () => void;
  onImportDone: () => void;
}) {
  const [activeTab, setActiveTab] = useState<"new" | "history" | "export">("new");
  const [step, setStep] = useState<ImportStep>("upload");
  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [duplicateStrategy, setDuplicateStrategy] = useState<DuplicateStrategy>("update");
  const [applyTagIds, setApplyTagIds] = useState<number[]>([]);
  const [captureDate, setCaptureDate] = useState<string>("");
  const [importProgress, setImportProgress] = useState(0);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [lastBatchId, setLastBatchId] = useState<number | null>(null);
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState<number>(0);
  const [mimeType, setMimeType] = useState<string>("");
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: customFieldDefs = [] } = trpc.customFields.listDefs.useQuery();
  const { data: tagsList = [] } = trpc.tags.list.useQuery();

  const [newTagName, setNewTagName] = useState("");

  // ─── Preview: collapsible + edição inline ────
  const [previewExpanded, setPreviewExpanded] = useState(true);
  const [previewFilter, setPreviewFilter] = useState<"all" | "errors">("all");
  const [editingCell, setEditingCell] = useState<{ originalIndex: number; sourceColumn: string } | null>(null);
  const [editDraft, setEditDraft] = useState("");

  const updateRawCell = useCallback((originalIndex: number, sourceColumn: string, value: string) => {
    setRawRows(prev => {
      const next = [...prev];
      next[originalIndex] = { ...next[originalIndex], [sourceColumn]: value };
      return next;
    });
  }, []);

  const beginEdit = (originalIndex: number, sourceColumn: string, currentValue: string) => {
    setEditingCell({ originalIndex, sourceColumn });
    setEditDraft(currentValue);
  };

  const commitEdit = () => {
    if (!editingCell) return;
    updateRawCell(editingCell.originalIndex, editingCell.sourceColumn, editDraft);
    setEditingCell(null);
    setEditDraft("");
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditDraft("");
  };

  const utils = trpc.useUtils();

  const createTagMut = trpc.tags.create.useMutation({
    onSuccess: (created: any) => {
      utils.tags.list.invalidate();
      // Auto-seleciona a tag recém-criada
      if (created?.id !== undefined) {
        setApplyTagIds(prev => [...prev, created.id]);
      } else if (Array.isArray(created) && created[0]?.id !== undefined) {
        setApplyTagIds(prev => [...prev, created[0].id]);
      }
      setNewTagName("");
      toast.success("Tag criada e adicionada à importação");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const handleCreateTag = () => {
    const name = newTagName.trim();
    if (!name) return;
    // Evita duplicatas exatas
    const existing = (tagsList as any[]).find((t: any) => t.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!applyTagIds.includes(existing.id)) {
        setApplyTagIds(prev => [...prev, existing.id]);
      }
      setNewTagName("");
      toast.info(`Tag "${existing.name}" já existe — selecionada`);
      return;
    }
    createTagMut.mutate({ name });
  };

  const bulkImportMut = trpc.contacts.bulkImport.useMutation({
    onSuccess: (res: any) => {
      setReport({
        total: res.total,
        created: res.created,
        updated: res.updated,
        skipped: res.skipped,
        errors: res.errors,
        duplicates: res.duplicates,
        errorDetails: res.errorDetails,
      });
      setLastBatchId(res.batchId ?? null);
      setStep("report");
      // Invalida histórico para refletir o novo lote ao trocar de aba
      utils.contacts.importsList.invalidate();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const reset = useCallback(() => {
    setStep("upload");
    setRawHeaders([]);
    setRawRows([]);
    setMappings([]);
    setDuplicateStrategy("update");
    setApplyTagIds([]);
    setCaptureDate("");
    setImportProgress(0);
    setReport(null);
    setLastBatchId(null);
    setFileName("");
    setFileSize(0);
    setMimeType("");
    if (fileRef.current) fileRef.current.value = "";
  }, []);

  // ─── Step 1: Upload ──────────────────────────────────────────────────────

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setFileSize(file.size);
    setMimeType(file.type || "");

    const isExcel = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");

    const reader = new FileReader();
    reader.onload = (ev) => {
      let headers: string[] = [];
      let rows: Record<string, string>[] = [];

      if (isExcel) {
        const data = new Uint8Array(ev.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonRows: Record<string, string>[] = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
        if (jsonRows.length > 0) {
          headers = Object.keys(jsonRows[0]);
          rows = jsonRows.map(row =>
            Object.fromEntries(Object.entries(row).map(([k, v]) => [k, String(v).trim()]))
          );
        }
      } else {
        const text = ev.target?.result as string;
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (lines.length > 0) {
          const sep = lines[0].includes(";") ? ";" : ",";
          headers = lines[0].split(sep).map(h => h.trim().replace(/^"|"$/g, ""));
          rows = lines.slice(1).map(line => {
            const vals = line.split(sep).map(v => v.trim().replace(/^"|"$/g, ""));
            return headers.reduce((obj, h, i) => ({ ...obj, [h]: vals[i] ?? "" }), {} as Record<string, string>);
          });
        }
      }

      rows = rows.filter(r => Object.values(r).some(v => v.trim() !== ""));

      if (rows.length === 0) {
        toast.error("Arquivo vazio ou sem dados validos");
        return;
      }

      setRawHeaders(headers);
      setRawRows(rows);

      const autoMappings: FieldMapping[] = headers.map(header => {
        let target = autoDetectMapping(header);
        if (target === "__ignore__") {
          target = detectByContent(rows, header);
        }
        return { sourceColumn: header, targetField: target };
      });

      const usedFields = new Set<string>();
      for (const m of autoMappings) {
        if (m.targetField !== "__ignore__" && usedFields.has(m.targetField)) {
          m.targetField = "__ignore__";
        }
        if (m.targetField !== "__ignore__") {
          usedFields.add(m.targetField);
        }
      }

      setMappings(autoMappings);
      setStep("mapping");
      toast.success(`${rows.length} linhas detectadas em "${file.name}"`);
    };

    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file, "UTF-8");
    }
  };

  // ─── Step 2: Mapping ────────────────────────────────────────────────────

  const updateMapping = (index: number, targetField: string) => {
    setMappings(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], targetField };
      return updated;
    });
  };

  const mappedFieldKeys = useMemo(() => {
    return new Set(mappings.filter(m => m.targetField !== "__ignore__").map(m => m.targetField));
  }, [mappings]);

  const hasPhone = mappedFieldKeys.has("phoneNumber");
  const hasEmail = mappedFieldKeys.has("email");
  const hasName = mappedFieldKeys.has("name");
  // Nome + PELO MENOS UM CANAL. Telefone deixou de ser obrigatório: lista
  // institucional de órgão público chega com o e-mail de compras/licitação e
  // nenhum telefone. Contato sem canal nenhum não teria como ser contatado —
  // por isso o "ou" e não a remoção completa da regra.
  const canProceed = hasName && (hasPhone || hasEmail);

  // ─── Step 3: Preview / Validation ────────────────────────────────────────

  const validatedRows = useMemo<RowValidation[]>(() => {
    if (step !== "preview") return [];

    return rawRows.map((row, originalIndex) => {
      const mapped: Record<string, string> = {};
      const errors: string[] = [];
      const warnings: string[] = [];

      for (const m of mappings) {
        if (m.targetField === "__ignore__") continue;
        const val = row[m.sourceColumn] ?? "";
        mapped[m.targetField] = val;
      }

      const hasValidEmail = Boolean(mapped.email?.trim() && validateEmail(mapped.email.trim()));

      if (mapped.phoneNumber?.trim()) {
        const phoneResult = normalizePhone(mapped.phoneNumber);
        mapped.phoneNumber = phoneResult.normalized;
        if (!phoneResult.valid) errors.push(`Telefone: ${phoneResult.warning}`);
        else if (phoneResult.warning) warnings.push(`Telefone: ${phoneResult.warning}`);
      } else {
        mapped.phoneNumber = "";
        // Sem telefone a linha só passa se o e-mail servir de canal — e o
        // e-mail precisa ser VÁLIDO, senão o contato entra sem forma alguma
        // de ser alcançado e sem chave para deduplicar na próxima importação.
        if (!hasValidEmail) {
          errors.push(mapped.email?.trim() ? "Sem telefone e e-mail invalido" : "Sem telefone e sem e-mail");
        } else {
          warnings.push("Sem telefone — nao recebe WhatsApp");
        }
      }

      if (!mapped.name?.trim()) {
        errors.push("Nome vazio");
      } else {
        // Padroniza primeiro nome para Title Case
        mapped.name = titleCaseName(mapped.name);
      }

      if (mapped.lastName?.trim()) {
        mapped.lastName = titleCaseName(mapped.lastName);
      }

      if (mapped.email?.trim() && !hasValidEmail) {
        warnings.push("E-mail invalido");
      }

      if (mapped.state && mapped.state.length > 2) {
        warnings.push("UF com mais de 2 caracteres");
        mapped.state = mapped.state.substring(0, 2).toUpperCase();
      } else if (mapped.state) {
        mapped.state = mapped.state.toUpperCase();
      }

      const status = errors.length > 0 ? "error" : warnings.length > 0 ? "warning" : "valid";
      return { originalIndex, row, mapped, status, errors, warnings };
    });
  }, [rawRows, mappings, step]);

  const validCount = validatedRows.filter(r => r.status !== "error").length;
  const warningCount = validatedRows.filter(r => r.status === "warning").length;
  const errorCount = validatedRows.filter(r => r.status === "error").length;

  // ─── Step 4: Import ──────────────────────────────────────────────────────

  const handleImport = () => {
    const importableRows = validatedRows
      .filter(r => r.status !== "error")
      .map(r => r.mapped);

    if (importableRows.length === 0) {
      toast.error("Nenhuma linha valida para importar");
      return;
    }

    setStep("importing");
    setImportProgress(10);

    const customFieldMappings: { fieldId: number; sourceKey: string }[] = [];
    for (const m of mappings) {
      if (m.targetField.startsWith("custom:")) {
        const fieldId = parseInt(m.targetField.replace("custom:", ""), 10);
        customFieldMappings.push({ fieldId, sourceKey: m.targetField });
      }
    }

    const progressInterval = setInterval(() => {
      setImportProgress(p => Math.min(p + 5, 90));
    }, 300);

    bulkImportMut.mutate({
      rows: importableRows.map(r => ({
        // null, não "": o unique (tenantId, phoneNumber) trata "" como valor
        // comum e o segundo contato sem telefone estouraria 23505.
        phoneNumber: r.phoneNumber?.trim() || null,
        name: r.name ?? "",
        lastName: r.lastName || null,
        email: r.email || null,
        contactType: r.contactType || null,
        companyName: r.companyName || null,
        companyType: r.companyType || null,
        sector: r.sector || null,
        jobTitle: r.jobTitle || null,
        city: r.city || null,
        state: r.state || null,
        notes: r.notes || null,
      })),
      duplicateStrategy,
      customFieldValues: customFieldMappings.length > 0
        ? importableRows.map(r => {
            const values: Record<string, string> = {};
            for (const cf of customFieldMappings) {
              if (r[cf.sourceKey]) values[String(cf.fieldId)] = r[cf.sourceKey];
            }
            return values;
          })
        : undefined,
      fileName: fileName || undefined,
      fileSize: fileSize || undefined,
      mimeType: mimeType || undefined,
      applyTagIds: applyTagIds.length > 0 ? applyTagIds : undefined,
      captureDate: captureDate || undefined,
      mappingSnapshot: mappings,
    }, {
      onSettled: () => {
        clearInterval(progressInterval);
        setImportProgress(100);
      },
    });
  };

  // ─── Download template ─────────────────────────────────────────────────

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["telefone", "nome", "sobrenome", "email", "tipo_contato", "orgao_empresa", "tipo_orgao", "setor", "cargo", "cidade", "estado", "observacoes"],
      ["5511999999999", "Joao", "Silva", "joao@email.com", "public_admin", "Camara Municipal de SP", "Camara Municipal", "Sec. de Administracao", "Pregoeiro", "Sao Paulo", "SP", ""],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contatos");
    XLSX.writeFile(wb, "modelo_contatos.xlsx");
  };

  // ─── Render ────────────────────────────────────────────────────────────

  const stepIndex = ["upload", "mapping", "preview", "importing", "report"].indexOf(step);
  const stepLabels = ["Upload", "Mapeamento", "Pre-visualizacao", "Importando", "Relatorio"];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6" />
            Listas de Contatos
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Importe, exporte e acompanhe o histórico das suas listas
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { reset(); onClose(); }}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Voltar para Contatos
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList>
          <TabsTrigger value="new">Nova importação</TabsTrigger>
          <TabsTrigger value="history">Histórico</TabsTrigger>
          <TabsTrigger value="export">Exportar</TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-4">
          <ImportHistoryTab />
        </TabsContent>

        <TabsContent value="export" className="mt-4">
          <ExportContactsTab />
        </TabsContent>

        <TabsContent value="new" className="mt-4 space-y-5">

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {stepLabels.map((label, i) => (
          <div key={label} className="flex items-center gap-2 flex-1">
            <div className={`flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-full transition-colors ${
              i < stepIndex ? "bg-green-100 text-green-700" :
              i === stepIndex ? "bg-primary text-primary-foreground" :
              "bg-muted text-muted-foreground"
            }`}>
              {i < stepIndex ? <Check className="h-4 w-4" /> : <span className="w-5 h-5 rounded-full border-2 border-current flex items-center justify-center text-xs">{i + 1}</span>}
              <span>{label}</span>
            </div>
            {i < stepLabels.length - 1 && <div className="flex-1 h-px bg-border" />}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === "upload" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-6">
                <div
                  className="border-2 border-dashed border-border rounded-xl p-16 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/20 transition-colors"
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-base font-medium">Clique ou arraste o arquivo aqui</p>
                  <p className="text-sm text-muted-foreground mt-2">Aceita .xlsx, .xls, .csv - Qualquer formato de colunas</p>
                  <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFileUpload} />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4 text-primary" />
                  <p className="text-sm font-medium">Modelo de planilha</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Baixe o modelo com as colunas sugeridas. Voce tambem pode usar qualquer planilha.
                </p>
                <Button variant="outline" size="sm" className="w-full" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-1.5" /> Baixar modelo .xlsx
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-2">
                <p className="text-sm font-medium text-blue-700">Deteccao inteligente</p>
                <p className="text-xs text-muted-foreground">
                  O sistema detecta automaticamente colunas como telefone, nome, email, cidade, etc.
                  Voce pode ajustar manualmente no proximo passo.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-2">
                <p className="text-sm font-medium">Campos do sistema</p>
                <div className="flex flex-wrap gap-1">
                  {SYSTEM_FIELDS.map(f => (
                    <Badge key={f.key} variant={f.required ? "default" : "secondary"} className="text-xs">
                      {f.label} {f.required && "*"}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Step 2: Mapping */}
      {step === "mapping" && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-base font-medium">Mapeamento de Colunas</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {rawRows.length} linhas em "<span className="font-medium">{fileName}</span>" - Associe cada coluna a um campo do sistema
                  </p>
                </div>
                <Badge variant={canProceed ? "default" : "destructive"} className="text-xs">
                  {canProceed ? "Pronto para continuar" : "Mapeie Nome e um canal"}
                </Badge>
              </div>

              {!canProceed && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2 mb-4">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  Mapeie <strong>Nome</strong> e pelo menos um canal — <strong>Telefone</strong> ou <strong>E-mail</strong> — para continuar.
                </div>
              )}

              {canProceed && !hasPhone && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2 mb-4">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  Nenhuma coluna de telefone mapeada. Os contatos entram só com e-mail e <strong>não recebem campanhas de WhatsApp</strong>.
                </div>
              )}

              <div className="border rounded-lg divide-y">
                {/* Header */}
                <div className="flex items-center gap-3 p-3 bg-muted/50 text-xs font-medium text-muted-foreground">
                  <div className="flex-1">Coluna da Planilha</div>
                  <div className="w-8" />
                  <div className="w-[240px] shrink-0">Campo do Sistema</div>
                  <div className="w-8 shrink-0 text-center">OK</div>
                </div>
                {mappings.map((m, idx) => {
                  const sampleValues = rawRows.slice(0, 3).map(r => r[m.sourceColumn] ?? "").filter(Boolean);
                  return (
                    <div key={idx} className="flex items-center gap-3 p-3 hover:bg-muted/20 transition-colors">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{m.sourceColumn}</p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {sampleValues.length > 0 ? sampleValues.join("  |  ") : "(vazio)"}
                        </p>
                      </div>

                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />

                      <div className="w-[240px] shrink-0">
                        <Select value={m.targetField} onValueChange={(v) => updateMapping(idx, v)}>
                          <SelectTrigger className="w-full text-sm h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__ignore__">
                              <span className="flex items-center gap-1.5 text-muted-foreground">
                                <X className="h-3 w-3" /> Ignorar coluna
                              </span>
                            </SelectItem>
                            {SYSTEM_FIELDS.map(f => (
                              <SelectItem key={f.key} value={f.key} disabled={mappedFieldKeys.has(f.key) && m.targetField !== f.key}>
                                <span className="flex items-center gap-1.5">
                                  {f.label} {f.required && <span className="text-red-500">*</span>}
                                </span>
                              </SelectItem>
                            ))}
                            {customFieldDefs.length > 0 && (
                              <>
                                {(customFieldDefs as any[]).map((cf: any) => (
                                  <SelectItem key={`custom:${cf.id}`} value={`custom:${cf.id}`}>
                                    <span className="flex items-center gap-1.5 text-violet-600">
                                      <Plus className="h-3 w-3" /> {cf.name}
                                    </span>
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="w-8 shrink-0 flex justify-center">
                        {m.targetField === "__ignore__" ? (
                          <X className="h-4 w-4 text-muted-foreground/40" />
                        ) : (
                          <Check className="h-4 w-4 text-green-600" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Duplicate strategy */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Label className="text-sm font-medium whitespace-nowrap">Quando encontrar duplicados (mesmo telefone):</Label>
                <Select value={duplicateStrategy} onValueChange={(v) => setDuplicateStrategy(v as DuplicateStrategy)}>
                  <SelectTrigger className="w-[240px] text-sm h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="update">Atualizar contato existente</SelectItem>
                    <SelectItem value="skip">Ignorar / Pular</SelectItem>
                    <SelectItem value="allow">Permitir duplicados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={reset}>
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Voltar ao Upload
            </Button>
            <Button onClick={() => setStep("preview")} disabled={!canProceed} size="lg">
              Pre-visualizar <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === "preview" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-base font-medium">Pre-visualizacao com Validacao</p>
              <p className="text-sm text-muted-foreground">{validatedRows.length} linhas analisadas</p>
            </div>
            <div className="flex gap-2">
              <Badge variant="outline" className="text-sm text-green-700 border-green-300 bg-green-50 px-3 py-1">
                <Check className="h-3.5 w-3.5 mr-1.5" /> {validCount} validos
              </Badge>
              {warningCount > 0 && (
                <Badge variant="outline" className="text-sm text-amber-700 border-amber-300 bg-amber-50 px-3 py-1">
                  <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> {warningCount} avisos
                </Badge>
              )}
              {errorCount > 0 && (
                <Badge variant="outline" className="text-sm text-red-700 border-red-300 bg-red-50 px-3 py-1">
                  <X className="h-3.5 w-3.5 mr-1.5" /> {errorCount} erros
                </Badge>
              )}
            </div>
          </div>

          {/* Bloco de Segmentação: tags a aplicar a todos os contatos do lote (em destaque, antes da tabela) */}
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <TagsIcon className="h-5 w-5 text-primary" />
                <Label className="text-base font-semibold">Tags desta lista</Label>
                {applyTagIds.length > 0 && (
                  <Badge variant="default" className="text-xs">
                    {applyTagIds.length} {applyTagIds.length === 1 ? "selecionada" : "selecionadas"}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                As tags marcadas serão aplicadas a todos os contatos criados <strong>e</strong> aos contatos existentes que forem atualizados por esta lista.
              </p>

              {/* Criar nova tag */}
              <div className="flex gap-2">
                <Input
                  placeholder="Criar nova tag..."
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCreateTag();
                    }
                  }}
                  disabled={createTagMut.isPending}
                  className="max-w-xs h-9"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim() || createTagMut.isPending}
                >
                  <Plus className="h-4 w-4 mr-1" /> Criar e adicionar
                </Button>
              </div>

              {/* Tags existentes */}
              {tagsList.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Nenhuma tag cadastrada ainda. Crie uma acima.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(tagsList as any[]).map((tag: any) => {
                    const selected = applyTagIds.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => {
                          setApplyTagIds(prev =>
                            prev.includes(tag.id)
                              ? prev.filter(id => id !== tag.id)
                              : [...prev, tag.id]
                          );
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          selected
                            ? "bg-primary text-primary-foreground border-primary shadow-sm"
                            : "bg-background hover:bg-muted border-border"
                        }`}
                        style={selected ? undefined : { borderLeft: `3px solid ${tag.color || "#3b82f6"}` }}
                      >
                        {selected && <Check className="h-3 w-3" />}
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Data de captação */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-primary" />
                <Label className="text-base font-semibold">Data de captação</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Defina a data em que esta lista foi captada. Essa data será aplicada como data de criação nos contatos novos <strong>e</strong> nos contatos existentes atualizados por esta lista. Se não informada, a data atual será usada.
              </p>
              <Input
                type="date"
                value={captureDate}
                onChange={(e) => setCaptureDate(e.target.value)}
                className="max-w-xs"
              />
            </CardContent>
          </Card>

          {/* Card colapsável com a pré-visualização */}
          <Card>
            <button
              type="button"
              onClick={() => setPreviewExpanded(p => !p)}
              className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                {previewExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">
                  {previewExpanded ? "Ocultar pré-visualização" : "Ver pré-visualização"}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({validatedRows.length} linhas — duplo clique para corrigir)
                </span>
              </div>
              {errorCount > 0 && (
                <Badge variant="outline" className="text-xs text-red-700 border-red-300 bg-red-50">
                  {errorCount} para revisar
                </Badge>
              )}
            </button>

            {previewExpanded && (
              <CardContent className="p-0 border-t">
                {/* Filtro: todas / só erros */}
                <div className="flex items-center gap-2 px-4 py-2 bg-muted/20 border-b">
                  <button
                    type="button"
                    onClick={() => setPreviewFilter("all")}
                    className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                      previewFilter === "all"
                        ? "bg-primary text-primary-foreground"
                        : "bg-background border hover:bg-muted"
                    }`}
                  >
                    Todas ({validatedRows.length})
                  </button>
                  {errorCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setPreviewFilter("errors")}
                      className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                        previewFilter === "errors"
                          ? "bg-red-600 text-white"
                          : "bg-background border border-red-200 text-red-700 hover:bg-red-50"
                      }`}
                    >
                      Apenas com erro ({errorCount})
                    </button>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground hidden sm:inline">
                    Dica: dê duplo clique numa célula para editar
                  </span>
                </div>

                <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/95 backdrop-blur-sm border-b z-10">
                      <tr>
                        <th className="p-3 text-left font-medium w-10">Status</th>
                        {mappings.filter(m => m.targetField !== "__ignore__").map((m, i) => {
                          const sysField = SYSTEM_FIELDS.find(f => f.key === m.targetField);
                          return (
                            <th key={i} className="p-3 text-left font-medium whitespace-nowrap">
                              {sysField?.label ?? m.targetField}
                            </th>
                          );
                        })}
                        <th className="p-3 text-left font-medium">Detalhes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filtered = previewFilter === "errors"
                          ? validatedRows.filter(r => r.status === "error")
                          : validatedRows;
                        const visible = filtered.slice(0, 200);
                        const visibleMappings = mappings.filter(m => m.targetField !== "__ignore__");
                        return (
                          <>
                            {visible.map((row) => (
                              <tr key={row.originalIndex} className={`border-b last:border-0 ${
                                row.status === "error" ? "bg-red-50/50" :
                                row.status === "warning" ? "bg-amber-50/30" : "hover:bg-muted/20"
                              }`}>
                                <td className="p-3">
                                  {row.status === "valid" && <Check className="h-4 w-4 text-green-600" />}
                                  {row.status === "warning" && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                                  {row.status === "error" && <X className="h-4 w-4 text-red-500" />}
                                </td>
                                {visibleMappings.map((m, j) => {
                                  const isEditing = editingCell?.originalIndex === row.originalIndex
                                    && editingCell?.sourceColumn === m.sourceColumn;
                                  const cellValue = row.mapped[m.targetField] ?? "";
                                  const rawValue = row.row[m.sourceColumn] ?? "";
                                  return (
                                    <td
                                      key={j}
                                      className="p-2 max-w-[200px]"
                                      onDoubleClick={() => beginEdit(row.originalIndex, m.sourceColumn, rawValue)}
                                      title={isEditing ? "" : "Duplo clique para editar"}
                                    >
                                      {isEditing ? (
                                        <Input
                                          autoFocus
                                          value={editDraft}
                                          onChange={(e) => setEditDraft(e.target.value)}
                                          onBlur={commitEdit}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                                            if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                                          }}
                                          className="h-8 text-sm"
                                        />
                                      ) : (
                                        <div className="group flex items-center gap-1.5 cursor-text truncate">
                                          <span className="truncate">{cellValue}</span>
                                          <Pencil className="h-3 w-3 text-muted-foreground/40 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
                                        </div>
                                      )}
                                    </td>
                                  );
                                })}
                                <td className="p-3">
                                  {(row.errors.length > 0 || row.warnings.length > 0) && (
                                    <span className="text-xs text-muted-foreground">
                                      {[...row.errors, ...row.warnings].join("; ")}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                            {filtered.length === 0 && (
                              <tr>
                                <td colSpan={visibleMappings.length + 2} className="p-8 text-center text-sm text-muted-foreground">
                                  Nenhuma linha {previewFilter === "errors" ? "com erro" : ""} para mostrar.
                                </td>
                              </tr>
                            )}
                            {filtered.length > 200 && (
                              <tr>
                                <td colSpan={visibleMappings.length + 2} className="p-3 text-center text-xs text-muted-foreground bg-muted/20">
                                  Mostrando 200 de {filtered.length} linhas. Filtre por erros para revisar todas.
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>

          {errorCount > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {errorCount} linhas com erro serão ignoradas. Apenas {validCount} contatos serão importados.
              Você pode corrigi-las dando duplo clique nas células acima.
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep("mapping")}>
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Voltar ao Mapeamento
            </Button>
            <Button onClick={handleImport} disabled={validCount === 0} size="lg">
              <Rocket className="h-4 w-4 mr-1.5" /> Importar {validCount} contatos
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Importing */}
      {step === "importing" && (
        <Card>
          <CardContent className="p-12">
            <div className="max-w-md mx-auto space-y-6">
              <div className="text-center">
                <div className="animate-spin h-10 w-10 border-3 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-lg font-medium">Importando contatos...</p>
                <p className="text-sm text-muted-foreground mt-1">Isso pode levar alguns instantes</p>
              </div>
              <Progress value={importProgress} className="h-3" />
              <p className="text-sm text-center text-muted-foreground">{importProgress}%</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Report */}
      {step === "report" && report && (
        <div className="space-y-5">
          <Card>
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-7 w-7 text-green-600" />
                </div>
                <p className="text-xl font-semibold">Importacao Concluida!</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="p-4 bg-green-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-green-700">{report.created}</p>
                  <p className="text-sm text-green-600 mt-1">Criados</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-blue-700">{report.updated}</p>
                  <p className="text-sm text-blue-600 mt-1">Atualizados</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-amber-700">{report.skipped}</p>
                  <p className="text-sm text-amber-600 mt-1">Ignorados</p>
                </div>
                <div className="p-4 bg-red-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-red-700">{report.errors}</p>
                  <p className="text-sm text-red-600 mt-1">Erros</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {report.duplicates > 0 && (
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                {report.duplicates} contatos duplicados detectados (estrategia: {
                  duplicateStrategy === "update" ? "atualizados" :
                  duplicateStrategy === "skip" ? "ignorados" : "permitidos"
                })
              </CardContent>
            </Card>
          )}

          {report.errorDetails.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <p className="text-sm font-medium text-red-700 mb-2">Detalhes dos erros:</p>
                <div className="max-h-[200px] overflow-y-auto space-y-1">
                  {report.errorDetails.slice(0, 20).map((err, i) => (
                    <p key={i} className="text-sm text-red-600">{err}</p>
                  ))}
                  {report.errorDetails.length > 20 && (
                    <p className="text-sm text-red-500 font-medium">... e mais {report.errorDetails.length - 20} erros</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {lastBatchId !== null && (
            <Card>
              <CardContent className="p-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Lote #{lastBatchId} salvo no histórico de importações.
                </p>
                <Button variant="link" size="sm" onClick={() => setActiveTab("history")}>
                  Ver no histórico →
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => { reset(); }}>
              Importar outro arquivo
            </Button>
            <Button onClick={() => { reset(); onClose(); onImportDone(); }}>
              Voltar para Contatos
            </Button>
          </div>
        </div>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
